import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SettingsService, 
  EmailNotificationSettings,
  PushNotificationSettings,
  AuctionSettings,
  PaymentInformation,
  CreatePaymentInformationRequest,
  UpdatePaymentInformationRequest,
  InvoiceInformation,
  CreateInvoiceInformationRequest,
  UpdateInvoiceInformationRequest,
  DeliveryAddress,
  CreateDeliveryAddressRequest,
  UpdateDeliveryAddressRequest,
} from '@/services/settings/settingsService';
import { isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';

export const useSettings = (options?: { enabledDeliveryAddresses?: boolean }) => {
  const queryClient = useQueryClient();

  // Email Notification Settings
  const emailNotificationSettingsQuery = useQuery({
    queryKey: ['settings', 'email-notifications'],
    queryFn: () => SettingsService.getEmailNotificationSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
  });

  const updateEmailNotificationSettingsMutation = useMutation({
    mutationFn: (settings: Partial<EmailNotificationSettings>) =>
      SettingsService.updateEmailNotificationSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'email-notifications'] });
    },
  });

  // Push Notification Settings (native only)
  const pushNotificationSettingsQuery = useQuery({
    queryKey: ['settings', 'push-notifications'],
    queryFn: () => SettingsService.getPushNotificationSettings(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isNativeCapacitorApp(),
  });

  const updatePushNotificationSettingsMutation = useMutation({
    mutationFn: (settings: Partial<PushNotificationSettings>) =>
      SettingsService.updatePushNotificationSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'push-notifications'] });
    },
  });

  // Auction Settings
  const auctionSettingsQuery = useQuery({
    queryKey: ['settings', 'auction'],
    queryFn: () => SettingsService.getAuctionSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
  });

  const updateAuctionSettingsMutation = useMutation({
    mutationFn: (settings: Partial<AuctionSettings>) =>
      SettingsService.updateAuctionSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'auction'] });
    },
  });

  // Payment Information
  const paymentInformationQuery = useQuery({
    queryKey: ['settings', 'payment'],
    queryFn: () => SettingsService.getPaymentInformation(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
  });

  const createPaymentInformationMutation = useMutation({
    mutationFn: (data: CreatePaymentInformationRequest) =>
      SettingsService.createPaymentInformation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'payment'] });
    },
  });

  const updatePaymentInformationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentInformationRequest }) =>
      SettingsService.updatePaymentInformation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'payment'] });
    },
  });

  const setDefaultPaymentInformationMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.setDefaultPaymentInformation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'payment'] });
    },
  });

  const deletePaymentInformationMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.deletePaymentInformation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'payment'] });
    },
  });

  // Invoice Information
  const invoiceInformationQuery = useQuery({
    queryKey: ['settings', 'invoice'],
    queryFn: () => SettingsService.getInvoiceInformation(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
  });

  const createInvoiceInformationMutation = useMutation({
    mutationFn: (data: CreateInvoiceInformationRequest) =>
      SettingsService.createInvoiceInformation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice'] });
    },
  });

  const updateInvoiceInformationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceInformationRequest }) =>
      SettingsService.updateInvoiceInformation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice'] });
    },
  });

  const setDefaultInvoiceInformationMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.setDefaultInvoiceInformation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice'] });
    },
  });

  const deleteInvoiceInformationMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.deleteInvoiceInformation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice'] });
    },
  });

  // Delivery Addresses
  const deliveryAddressesQuery = useQuery({
    queryKey: ['settings', 'delivery'],
    queryFn: () => SettingsService.getDeliveryAddresses(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
    enabled: options?.enabledDeliveryAddresses !== false,
  });

  const createDeliveryAddressMutation = useMutation({
    mutationFn: (data: CreateDeliveryAddressRequest) =>
      SettingsService.createDeliveryAddress(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery'] });
    },
  });

  const updateDeliveryAddressMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeliveryAddressRequest }) =>
      SettingsService.updateDeliveryAddress(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery'] });
    },
  });

  const setDefaultDeliveryAddressMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.setDefaultDeliveryAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery'] });
    },
  });

  const deleteDeliveryAddressMutation = useMutation({
    mutationFn: (id: string) =>
      SettingsService.deleteDeliveryAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery'] });
    },
  });

  return {
    // Email Notification Settings
    emailNotificationSettings: emailNotificationSettingsQuery.data,
    isLoadingEmailNotificationSettings: emailNotificationSettingsQuery.isLoading,
    isFetchingEmailNotificationSettings: emailNotificationSettingsQuery.isFetching,
    emailNotificationSettingsError: emailNotificationSettingsQuery.error,
    refetchEmailNotificationSettings: emailNotificationSettingsQuery.refetch,
    updateEmailNotificationSettings: updateEmailNotificationSettingsMutation.mutate,
    updateEmailNotificationSettingsAsync: updateEmailNotificationSettingsMutation.mutateAsync,
    isUpdatingEmailNotificationSettings: updateEmailNotificationSettingsMutation.isPending,
    updateEmailNotificationSettingsError: updateEmailNotificationSettingsMutation.error,

    // Push Notification Settings (native)
    pushNotificationSettings: pushNotificationSettingsQuery.data,
    isLoadingPushNotificationSettings: pushNotificationSettingsQuery.isLoading,
    isFetchingPushNotificationSettings: pushNotificationSettingsQuery.isFetching,
    pushNotificationSettingsError: pushNotificationSettingsQuery.error,
    refetchPushNotificationSettings: pushNotificationSettingsQuery.refetch,
    updatePushNotificationSettings: updatePushNotificationSettingsMutation.mutate,
    updatePushNotificationSettingsAsync: updatePushNotificationSettingsMutation.mutateAsync,
    isUpdatingPushNotificationSettings: updatePushNotificationSettingsMutation.isPending,
    updatePushNotificationSettingsError: updatePushNotificationSettingsMutation.error,

    // Auction Settings
    auctionSettings: auctionSettingsQuery.data,
    isLoadingAuctionSettings: auctionSettingsQuery.isLoading,
    isFetchingAuctionSettings: auctionSettingsQuery.isFetching,
    auctionSettingsError: auctionSettingsQuery.error,
    refetchAuctionSettings: auctionSettingsQuery.refetch,
    updateAuctionSettings: updateAuctionSettingsMutation.mutate,
    updateAuctionSettingsAsync: updateAuctionSettingsMutation.mutateAsync,
    isUpdatingAuctionSettings: updateAuctionSettingsMutation.isPending,
    updateAuctionSettingsError: updateAuctionSettingsMutation.error,

    // Payment Information
    paymentInformation: paymentInformationQuery.data,
    isLoadingPaymentInformation: paymentInformationQuery.isLoading,
    isFetchingPaymentInformation: paymentInformationQuery.isFetching,
    paymentInformationError: paymentInformationQuery.error,
    refetchPaymentInformation: paymentInformationQuery.refetch,
    createPaymentInformation: createPaymentInformationMutation.mutate,
    createPaymentInformationAsync: createPaymentInformationMutation.mutateAsync,
    isCreatingPaymentInformation: createPaymentInformationMutation.isPending,
    createPaymentInformationError: createPaymentInformationMutation.error,
    updatePaymentInformation: updatePaymentInformationMutation.mutate,
    updatePaymentInformationAsync: updatePaymentInformationMutation.mutateAsync,
    isUpdatingPaymentInformation: updatePaymentInformationMutation.isPending,
    updatePaymentInformationError: updatePaymentInformationMutation.error,
    setDefaultPaymentInformation: setDefaultPaymentInformationMutation.mutate,
    setDefaultPaymentInformationAsync: setDefaultPaymentInformationMutation.mutateAsync,
    isSettingDefaultPaymentInformation: setDefaultPaymentInformationMutation.isPending,
    deletePaymentInformation: deletePaymentInformationMutation.mutate,
    deletePaymentInformationAsync: deletePaymentInformationMutation.mutateAsync,
    isDeletingPaymentInformation: deletePaymentInformationMutation.isPending,
    deletePaymentInformationError: deletePaymentInformationMutation.error,

    // Invoice Information
    invoiceInformation: invoiceInformationQuery.data,
    isLoadingInvoiceInformation: invoiceInformationQuery.isLoading,
    isFetchingInvoiceInformation: invoiceInformationQuery.isFetching,
    invoiceInformationError: invoiceInformationQuery.error,
    refetchInvoiceInformation: invoiceInformationQuery.refetch,
    createInvoiceInformation: createInvoiceInformationMutation.mutate,
    createInvoiceInformationAsync: createInvoiceInformationMutation.mutateAsync,
    isCreatingInvoiceInformation: createInvoiceInformationMutation.isPending,
    createInvoiceInformationError: createInvoiceInformationMutation.error,
    updateInvoiceInformation: updateInvoiceInformationMutation.mutate,
    updateInvoiceInformationAsync: updateInvoiceInformationMutation.mutateAsync,
    isUpdatingInvoiceInformation: updateInvoiceInformationMutation.isPending,
    updateInvoiceInformationError: updateInvoiceInformationMutation.error,
    setDefaultInvoiceInformation: setDefaultInvoiceInformationMutation.mutate,
    setDefaultInvoiceInformationAsync: setDefaultInvoiceInformationMutation.mutateAsync,
    isSettingDefaultInvoiceInformation: setDefaultInvoiceInformationMutation.isPending,
    deleteInvoiceInformation: deleteInvoiceInformationMutation.mutate,
    deleteInvoiceInformationAsync: deleteInvoiceInformationMutation.mutateAsync,
    isDeletingInvoiceInformation: deleteInvoiceInformationMutation.isPending,
    deleteInvoiceInformationError: deleteInvoiceInformationMutation.error,

    // Delivery Addresses
    deliveryAddresses: deliveryAddressesQuery.data,
    isLoadingDeliveryAddresses: deliveryAddressesQuery.isLoading,
    isFetchingDeliveryAddresses: deliveryAddressesQuery.isFetching,
    deliveryAddressesError: deliveryAddressesQuery.error,
    refetchDeliveryAddresses: deliveryAddressesQuery.refetch,
    createDeliveryAddress: createDeliveryAddressMutation.mutate,
    createDeliveryAddressAsync: createDeliveryAddressMutation.mutateAsync,
    isCreatingDeliveryAddress: createDeliveryAddressMutation.isPending,
    createDeliveryAddressError: createDeliveryAddressMutation.error,
    updateDeliveryAddress: updateDeliveryAddressMutation.mutate,
    updateDeliveryAddressAsync: updateDeliveryAddressMutation.mutateAsync,
    isUpdatingDeliveryAddress: updateDeliveryAddressMutation.isPending,
    updateDeliveryAddressError: updateDeliveryAddressMutation.error,
    setDefaultDeliveryAddress: setDefaultDeliveryAddressMutation.mutate,
    setDefaultDeliveryAddressAsync: setDefaultDeliveryAddressMutation.mutateAsync,
    isSettingDefaultDeliveryAddress: setDefaultDeliveryAddressMutation.isPending,
    deleteDeliveryAddress: deleteDeliveryAddressMutation.mutate,
    deleteDeliveryAddressAsync: deleteDeliveryAddressMutation.mutateAsync,
    isDeletingDeliveryAddress: deleteDeliveryAddressMutation.isPending,
    deleteDeliveryAddressError: deleteDeliveryAddressMutation.error,
  };
};

